import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../../services/notiflix.service';
import UserInvestmentService from '../../services/user-investment.service';
import {
  clearSelectedTransaction,
  pendingBankTransfer,
} from '../../actions/bankTransfer';
import { useDispatch, useSelector } from 'react-redux';
import { CloseButtonSvgIcon } from './IconProvider';
import ButtonLoader from './auth/ButtonLoader';

const BankTransferModal = ({
  selectedTransaction,
  closeModal,
  refreshInvestInput,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['projectDetails', 'common']);
  const { pendingTransactions } = useSelector(
    (state) => state.bankTransferReducer,
  );
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isLaterLoading, setIsLaterLoading] = useState(false);
  const [isPaidLoading, setIsPaidLoading] = useState(false);

  const handleCancelBankTransaction = async () => {
    try {
      setIsCancelLoading(true);
      const response = await UserInvestmentService.cancelBankTransaction({
        order_id: selectedTransaction.order_id,
      });
      if (response.status === 'failure') {
        Alert.error(response.message);
        return;
      }
      dispatch(clearSelectedTransaction());
      dispatch(pendingBankTransfer());
      closeModal();
      refreshInvestInput();
    } catch (error) {
      Alert.error(error.message);
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleDoLater = async () => {
    try {
      setIsLaterLoading(true);
      dispatch(clearSelectedTransaction());
      dispatch(pendingBankTransfer());
      closeModal();
      refreshInvestInput();
    } catch (error) {
      Alert.error(error.message);
    } finally {
      setIsLaterLoading(false);
    }
  };

  const handlePaidBankTransfer = async () => {
    try {
      setIsPaidLoading(true);
      const response = await UserInvestmentService.updateTransactionStatus(
        selectedTransaction.order_id,
      );
      if (response.status === 'failure') {
        Alert.error(response.message);
        return;
      }
      dispatch(clearSelectedTransaction());
      dispatch(pendingBankTransfer());
      closeModal();
      refreshInvestInput();
    } catch (error) {
      Alert.error(error.message);
    } finally {
      setIsPaidLoading(false);
    }
  };

  const handleCloseModal = () => {
    dispatch(clearSelectedTransaction());
    dispatch(pendingBankTransfer());
    closeModal();
    refreshInvestInput();
  };

  const isNewTransaction = selectedTransaction?.order_id
    ? !pendingTransactions.some(
        (t) => t.order_id === selectedTransaction.order_id,
      )
    : true;
  const isDoLaterDisabled = isNewTransaction && pendingTransactions.length >= 5;

  return (
    <>
      <div className="flex justify-center items-center overflow-x-hidden overflow-y-hidden fixed inset-0 z-50 outline-none focus:outline-none top-4">
        <div className="relative w-full mx-3 my-4 max-w-3xl">
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white text-black p-3">
            <div className="flex justify-between items-center py-1 px-6">
              <div></div>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black text-3xl leading-none font-semibold outline-none focus:outline-none absolute right-1 top-3 z-10 flex justify-center items-center"
                onClick={handleCloseModal}
              >
                <span className="bg-transparent text-black w-10 block outline-none focus:outline-none">
                  <CloseButtonSvgIcon />
                </span>
              </button>
            </div>
            <div className="relative p-6 flex-auto">
              <h4 className="text-left mb-4 font-bold text-xl">
                {t('awaiting_funds')}
              </h4>
              <p className="text-[18px] leading-[27px] text-left mb-4">
                {t('bank_instruction_note')}
              </p>
              <p className="text-[18px] leading-[27px] mb-6 text-left">
                {t('required_details_below')}
              </p>
              <div className="flex gap-8">
                <div>
                  <img
                    src="/img/sepa-transfer.png"
                    alt=""
                    width={200}
                    height={200}
                  />
                </div>
                <div className="me-10">
                  <div className="space-y-1">
                    <div className="flex border border-black p-2">
                      <span className="text-[18px] w-1/3">
                        {t('bank_name')}:
                      </span>
                      <span className="w-2/3 text-[18px]">
                        {selectedTransaction?.bankName || 'N/A'}
                      </span>
                    </div>
                    <div className="flex border border-black p-2">
                      <span className="text-[18px] w-1/3">{t('iban')}:</span>
                      <span className="w-2/3 text-[18px]">
                        {selectedTransaction?.bankAccount || 'N/A'}
                      </span>
                    </div>
                    <div className="flex border border-black p-2">
                      <span className="text-[18px] w-1/3">{t('amount')}:</span>
                      <span className="w-2/3 text-[18px]">
                        € {selectedTransaction?.amount || 'N/A'}
                      </span>
                    </div>
                    <div className="flex border border-black p-2">
                      <span className="text-[18px] w-1/3">
                        {t('payment_reference')}:
                      </span>
                      <span className="w-2/3 text-[18px]">
                        {selectedTransaction?.paymentReference || 'N/A'}
                      </span>
                    </div>
                    <div className="flex border border-black p-2">
                      <span className="text-[18px] w-1/3">
                        {t('expiry_date')}:
                      </span>
                      <span className="w-2/3 text-[18px]">
                        {selectedTransaction?.expiryDate || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <p
                    className="text-[18px] leading-[27px] text-start text-brandBlue mt-4"
                    dangerouslySetInnerHTML={{ __html: t('email_sent_info') }}
                  ></p>
                </div>
              </div>
              <p
                className="text-[18px] leading-[27px] mt-4 text-left"
                dangerouslySetInnerHTML={{
                  __html: t('transfer_timeline_note'),
                }}
              ></p>
            </div>

            <div className="py-3 px-8 text-center flex gap-4 justify-end">
              <button
                className="bg-white hover:bg-gray-200 active:bg-gray-100 text-[#0B243F] border border-[#0B243F] font-semibold text-base rounded-md transition-all disabled:opacity-50"
                type="button"
                onClick={handleCancelBankTransaction}
                disabled={isCancelLoading || !selectedTransaction?.order_id}
              >
                {isCancelLoading ? (
                  <ButtonLoader title={t('processing')} />
                ) : (
                  t('cancel_order')
                )}
              </button>

              <button
                className={`bg-white hover:bg-gray-200 active:bg-gray-100 text-[#0B243F] border border-[#0B243F] font-semibold text-base rounded-md transition-all disabled:opacity-50 ${
                  isDoLaterDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                type="button"
                onClick={handleDoLater}
                disabled={isDoLaterDisabled || isLaterLoading}
              >
                {isLaterLoading ? (
                  <ButtonLoader title={t('processing')} />
                ) : (
                  t('i_will_do_later')
                )}
              </button>
              <button
                onClick={handlePaidBankTransfer}
                className="bg-[#A7D760] hover:bg-[#97C05B] active:bg-[#8DB158] text-[#0B243F] text-base font-semibold rounded-md transition-all disabled:opacity-50"
                type="button"
                disabled={isPaidLoading || !selectedTransaction?.order_id}
              >
                {isPaidLoading ? (
                  <ButtonLoader title={t('processing')} />
                ) : (
                  t('i_have_paid')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
};

export default BankTransferModal;
